import { useEffect, useMemo, useState, useCallback } from "react";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  User, Save, Loader2, Plus, Trash2, Upload, Download, CalendarDays, Clock,
  BadgeCheck, AlertTriangle, DollarSign, FileText, TrendingUp, ChevronLeft,
  Check, X, Fingerprint,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PdksEmployeeTab } from "@/pages/Pdks";
import { EmployeeWageCard } from "@/pages/Payroll";

const DOC_CATS = {
  sgk_giris: "SGK Giriş",
  sozlesme: "Sözleşme",
  kimlik: "Kimlik",
  diploma: "Diploma",
  sertifika: "Sertifika",
  saglik_raporu: "Sağlık Raporu",
  imzali_evrak: "İmzalı Evrak",
  diger: "Diğer",
};

const LEAVE_TYPES = {
  yillik: "Yıllık",
  hastalik: "Hastalık",
  mazeret: "Mazeret",
  ucretsiz: "Ücretsiz",
  ebeveyn: "Ebeveyn",
  diger: "Diğer",
};

const LEAVE_STATUS = {
  beklemede: { label: "Beklemede", color: "text-amber-500 border-amber-800/60 bg-amber-950/40" },
  onaylandi: { label: "Onaylandı", color: "text-emerald-400 border-emerald-800/60 bg-emerald-950/40" },
  reddedildi: { label: "Reddedildi", color: "text-rose-400 border-rose-800/60 bg-rose-950/40" },
  iptal: { label: "İptal", color: "text-slate-400 border-slate-700 bg-slate-800/40" },
};

const ADVANCE_STATUS = {
  beklemede: { label: "Beklemede", color: "text-amber-500" },
  onaylandi: { label: "Onaylandı", color: "text-emerald-400" },
  reddedildi: { label: "Reddedildi", color: "text-rose-400" },
  odendi: { label: "Ödendi", color: "text-blue-400" },
};

const SEVERITY = {
  dusuk: { label: "Düşük", color: "text-slate-400" },
  orta: { label: "Orta", color: "text-amber-500" },
  yuksek: { label: "Yüksek", color: "text-blue-500" },
  tutanak: { label: "Tutanak", color: "text-rose-500" },
};

const fmtMoney = (n) => (Number(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMin = (m) => {
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}s ${r}dk`;
};

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can("employees", "edit");
  const [emp, setEmp] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [meta, setMeta] = useState({ roles: [], role_labels: {}, departments: [], department_labels: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m] = await Promise.all([
        api.get(`/hr/employees/${id}/dashboard`),
        api.get(`/employees/meta`),
      ]);
      setEmp(d.data.employee);
      setDashboard(d.data);
      setMeta(m.data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/calisan");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const savePatch = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/employees/${id}`, patch);
      setEmp(data);
      toast.success("Kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading || !emp) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    );
  }

  const initials = emp.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div>
      <PageHeader
        title={emp.name}
        subtitle={`${meta.role_labels[emp.role] || emp.role}${emp.department ? " · " + (meta.department_labels[emp.department] || emp.department) : ""}`}
        breadcrumbs={["Modüller", "Çalışanlar", emp.name]}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate("/calisan")}
            className="rounded-sm border-slate-700"
            data-testid="back-to-employees"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Listeye Dön
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-5 max-w-7xl mx-auto">
        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Kalan Yıllık İzin" value={`${dashboard.leave_remaining} gün`}
            hint={`Hak: ${dashboard.leave_entitlement} gün`} accent="emerald" />
          <StatCard label="Bekleyen Talepler" value={`${dashboard.pending_leaves + dashboard.pending_advances}`}
            hint={`${dashboard.pending_leaves} izin · ${dashboard.pending_advances} avans`} accent="amber" />
          <StatCard label="Belgeler" value={dashboard.document_count} hint="Kayıtlı doküman" accent="blue" />
          <StatCard label="Son Bordro" value={dashboard.last_payroll_period || "—"}
            hint={dashboard.last_payroll_net ? `Net: ₺${fmtMoney(dashboard.last_payroll_net)}` : "Kayıt yok"} accent="orange" />
        </div>

        {/* Header — avatar + hızlı bilgi */}
        <Card className="rounded-sm border-border p-5 flex items-start gap-4 flex-wrap">
          <div className="w-16 h-16 rounded-full bg-blue-950/40 border border-blue-900 flex items-center justify-center text-blue-400 font-bold text-lg">
            {initials}
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-xl font-bold">{emp.name}</div>
            <div className="text-xs font-mono text-muted-foreground mt-1">
              {emp.email || "—"} {emp.phone && `· ${emp.phone}`}
            </div>
            {emp.position && <div className="text-xs mt-1 text-slate-400">Ünvan: {emp.position}</div>}
            {emp.start_date && <div className="text-xs mt-1 text-slate-400">İşe başlama: {emp.start_date}</div>}
          </div>
          <Badge className={`rounded-sm text-[10px] uppercase tracking-wider ${emp.status === "aktif" ? "bg-emerald-950 text-emerald-300 border-emerald-800" : "bg-slate-800 text-slate-400 border-slate-700"}`}>
            {emp.status}
          </Badge>
          <RevokeAccessButton employee={emp} />
        </Card>

        <Tabs defaultValue="genel" className="w-full">
          <TabsList className="rounded-sm bg-muted/40 border border-border flex-wrap h-auto">
            <TabsTrigger value="genel" data-testid="tab-genel" className="rounded-sm">
              <User className="w-3.5 h-3.5 mr-1" /> Genel Bilgi
            </TabsTrigger>
            <TabsTrigger value="belgeler" data-testid="tab-belgeler" className="rounded-sm">
              <FileText className="w-3.5 h-3.5 mr-1" /> Belgeler
            </TabsTrigger>
            <TabsTrigger value="puantaj" data-testid="tab-puantaj" className="rounded-sm">
              <Clock className="w-3.5 h-3.5 mr-1" /> Puantaj
            </TabsTrigger>
            <TabsTrigger value="pdks" data-testid="tab-pdks" className="rounded-sm">
              <Fingerprint className="w-3.5 h-3.5 mr-1" /> PDKS
            </TabsTrigger>
            <TabsTrigger value="izin" data-testid="tab-izin" className="rounded-sm">
              <CalendarDays className="w-3.5 h-3.5 mr-1" /> İzinler
            </TabsTrigger>
            <TabsTrigger value="avans" data-testid="tab-avans" className="rounded-sm">
              <DollarSign className="w-3.5 h-3.5 mr-1" /> Avans / Bordro
            </TabsTrigger>
            <TabsTrigger value="ucret" data-testid="tab-ucret" className="rounded-sm">
              <DollarSign className="w-3.5 h-3.5 mr-1" /> Ücret Bilgileri
            </TabsTrigger>
            <TabsTrigger value="performans" data-testid="tab-performans" className="rounded-sm">
              <TrendingUp className="w-3.5 h-3.5 mr-1" /> Performans / Uyarı
            </TabsTrigger>
          </TabsList>

          <TabsContent value="genel" className="mt-5">
            <GeneralTab emp={emp} meta={meta} saving={saving} onSave={savePatch} canEdit={canEdit} />
          </TabsContent>
          <TabsContent value="belgeler" className="mt-5">
            <DocumentsTab employeeId={id} onChange={load} />
          </TabsContent>
          <TabsContent value="puantaj" className="mt-5">
            <AttendanceTab employeeId={id} onChange={load} />
          </TabsContent>
          <TabsContent value="pdks" className="mt-5">
            <PdksEmployeeTab employeeId={id} />
          </TabsContent>
          <TabsContent value="izin" className="mt-5">
            <LeavesTab employeeId={id} onChange={load} />
          </TabsContent>
          <TabsContent value="avans" className="mt-5">
            <AdvancesAndPayrollTab employeeId={id} onChange={load} />
          </TabsContent>
          <TabsContent value="ucret" className="mt-5 space-y-5">
            <EmployeeWageCard employeeId={id} />
            <CommissionSettingsCard emp={emp} saving={saving} onSave={savePatch} canEdit={canEdit} />
          </TabsContent>
          <TabsContent value="performans" className="mt-5">
            <PerformanceTab employeeId={id} onChange={load} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ---- KPI Card ----
function StatCard({ label, value, hint, accent = "orange" }) {
  const accents = {
    orange: "text-blue-400 border-blue-900",
    emerald: "text-emerald-400 border-emerald-900",
    blue: "text-blue-400 border-blue-900",
    amber: "text-amber-400 border-amber-900",
  };
  return (
    <Card className={`rounded-sm border-border p-3 ${accents[accent]}`}>
      <div className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground">{label}</div>
      <div className="text-lg font-bold mt-1">{value}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>
    </Card>
  );
}

// ============================================================================
// GENEL BİLGİ
// ============================================================================
function GeneralTab({ emp, meta, saving, onSave, canEdit }) {
  const [form, setForm] = useState(emp);
  useEffect(() => { setForm(emp); }, [emp]);
  const patch = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const dirty = JSON.stringify(form) !== JSON.stringify(emp);

  return (
    <Card className="rounded-sm border-border p-5 space-y-4 max-w-4xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Ad Soyad" value={form.name} onChange={(v) => patch("name", v)} testId="gi-name" />
        <Field label="TC Kimlik No" value={form.tc_no} onChange={(v) => patch("tc_no", v)} testId="gi-tc" />
        <Field label="Doğum Tarihi" type="date" value={form.birth_date || ""} onChange={(v) => patch("birth_date", v)} testId="gi-birth" />
        <Field label="E-posta" type="email" value={form.email} onChange={(v) => patch("email", v)} testId="gi-email" />
        <Field label="Telefon" value={form.phone} onChange={(v) => patch("phone", v)} testId="gi-phone" />
        <Field label="Adres" value={form.address} onChange={(v) => patch("address", v)} testId="gi-address" />
        <Field label="IBAN" value={form.iban} onChange={(v) => patch("iban", v)} testId="gi-iban" />
        <Field label="SGK No" value={form.sgk_no} onChange={(v) => patch("sgk_no", v)} testId="gi-sgk" />
        <Field label="Acil Durum - Ad" value={form.emergency_contact_name} onChange={(v) => patch("emergency_contact_name", v)} testId="gi-eca" />
        <Field label="Acil Durum - Telefon" value={form.emergency_contact_phone} onChange={(v) => patch("emergency_contact_phone", v)} testId="gi-ecp" />
        <SelectField label="Rol" value={form.role} onChange={(v) => patch("role", v)}
          options={meta.roles.map((r) => ({ value: r, label: meta.role_labels[r] || r }))}
          testId="gi-role" />
        <SelectField label="Departman" value={form.department || "none"} onChange={(v) => patch("department", v === "none" ? "" : v)}
          options={[{ value: "none", label: "— Departmansız —" }, ...meta.departments.map((d) => ({ value: d, label: meta.department_labels[d] || d }))]}
          testId="gi-dept" />
        <Field label="Ünvan / Pozisyon" value={form.position} onChange={(v) => patch("position", v)} testId="gi-position" />
        <SelectField label="Çalışma Tipi" value={form.employment_type} onChange={(v) => patch("employment_type", v)}
          options={[
            { value: "tam_zamanli", label: "Tam Zamanlı" },
            { value: "yari_zamanli", label: "Yarı Zamanlı" },
            { value: "sozlesmeli", label: "Sözleşmeli" },
            { value: "stajyer", label: "Stajyer" },
            { value: "danisman", label: "Danışman" },
          ]} testId="gi-emp-type" />
        <Field label="İşe Başlama Tarihi" type="date" value={form.start_date || ""} onChange={(v) => patch("start_date", v)} testId="gi-start" />
        <Field label="Maaş (₺)" type="number" value={form.salary} onChange={(v) => patch("salary", Number(v) || 0)} testId="gi-salary" />
        <Field label="Yıllık İzin Hakkı (gün)" type="number" value={form.annual_leave_entitlement} onChange={(v) => patch("annual_leave_entitlement", Number(v) || 14)} testId="gi-leave-ent" />
      </div>
      <div>
        <Label className="text-xs uppercase font-mono text-muted-foreground">Notlar</Label>
        <Textarea rows={2} value={form.notes || ""} onChange={(e) => patch("notes", e.target.value)}
          className="rounded-sm mt-1" data-testid="gi-notes" />
      </div>
      {canEdit ? <div className="flex justify-end pt-2 border-t border-border/60">
        <Button
          data-testid="save-general-btn"
          disabled={!dirty || saving}
          onClick={() => {
            const { id: _id, created_at: _ca, ...patch } = form;
            onSave(patch);
          }}
          className="rounded-sm bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Değişiklikleri Kaydet
        </Button>
      </div> : <div className="text-[11px] text-muted-foreground pt-2 border-t border-border/60" data-testid="general-readonly-note">Personel düzenleme yetkiniz yok (employees.edit) — yalnız görüntüleme.</div>}
    </Card>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <Label className="text-xs uppercase font-mono text-muted-foreground">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="rounded-sm mt-1" data-testid={testId} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase font-mono text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm mt-1" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ============================================================================
// PRİM / KOMİSYON AYARLARI (Ücret Bilgileri sekmesi) — employees kaydında tutulur
// ============================================================================
function CommissionSettingsCard({ emp, saving, onSave, canEdit }) {
  const [active, setActive] = useState(!!emp.commission_enabled);
  const [basis, setBasis] = useState(emp.commission_basis || "invoiced");
  const [rate, setRate] = useState(String(emp.commission_rate ?? 0));
  useEffect(() => {
    setActive(!!emp.commission_enabled);
    setBasis(emp.commission_basis || "invoiced");
    setRate(String(emp.commission_rate ?? 0));
  }, [emp.id, emp.commission_enabled, emp.commission_basis, emp.commission_rate]);

  const submit = () => {
    const r = parseFloat(String(rate).replace(",", "."));
    if (isNaN(r) || r < 0 || r > 100) { toast.error("Prim oranı 0–100 arasında olmalı"); return; }
    onSave({ commission_enabled: active, commission_basis: basis, commission_rate: r });
  };

  return (
    <Card className="rounded-sm border-border p-5" data-testid="commission-settings-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-500" /> Prim / Komisyon Ayarları
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Bu personel müşteri temsilcisi olduğunda satışlarından prim hesaplanır.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-mono uppercase">Prim {active ? "Açık" : "Kapalı"}</span>
          <Switch checked={active} onCheckedChange={setActive} data-testid="commission-active-switch" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <SelectField
          label="Prim Matrahı"
          value={basis}
          onChange={setBasis}
          testId="commission-basis-select"
          options={[
            { value: "invoiced", label: "Faturalanan Satış" },
            { value: "collected", label: "Tahsilat" },
          ]}
        />
        <Field
          label="Prim Oranı (%)"
          value={rate}
          onChange={setRate}
          testId="commission-rate-input"
        />
        <Button onClick={submit} disabled={saving || !canEdit} title={canEdit ? undefined : "Personel düzenleme yetkiniz yok (employees.edit)"} data-testid="commission-settings-save"
          className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Kaydet
        </Button>
      </div>
    </Card>
  );
}

// ============================================================================
// BELGELER
// ============================================================================
function DocumentsTab({ employeeId, onChange }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("diger");
  const [label, setLabel] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/hr/employees/${employeeId}/documents`);
      setDocs(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      fd.append("label", label || "");
      await api.post(`/hr/employees/${employeeId}/documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Belge yüklendi");
      setLabel("");
      load();
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (docId) => {
    if (!window.confirm("Bu belge silinsin mi?")) return;
    try {
      await api.delete(`/hr/documents/${docId}`);
      toast.success("Silindi");
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="rounded-sm border-border p-5 space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs">Kategori</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="doc-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(DOC_CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs">Etiket (opsiyonel)</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} className="rounded-sm mt-1"
            placeholder="örn: 2026 vergi levhası" data-testid="doc-label" />
        </div>
        <label className={`inline-flex items-center gap-2 px-4 h-9 rounded-sm cursor-pointer text-xs font-semibold uppercase tracking-wider transition-colors ${uploading ? "bg-slate-700 text-slate-400" : "bg-blue-600 hover:bg-blue-500 text-white"}`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          Belge Yükle
          <input type="file" className="hidden" onChange={onFile} disabled={uploading} data-testid="doc-upload-input" />
        </label>
      </div>

      {docs.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-8">Henüz belge yüklenmedi.</div>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Ad</th>
                <th className="text-left px-3 py-2">Kategori</th>
                <th className="text-left px-3 py-2">Etiket</th>
                <th className="text-right px-3 py-2">Boyut</th>
                <th className="text-left px-3 py-2">Yükleyen</th>
                <th className="text-right px-3 py-2">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {docs.map((d) => (
                <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{d.original_filename}</td>
                  <td className="px-3 py-2">
                    <Badge className="rounded-sm text-[10px] bg-blue-950 text-blue-300 border-blue-800">
                      {DOC_CATS[d.category] || d.category}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">{d.label || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{(d.size / 1024).toFixed(1)} KB</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{d.uploaded_by}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <a
                        href={`${API}/hr/documents/${d.id}/download`}
                        target="_blank" rel="noreferrer"
                        className="p-1.5 text-blue-400 hover:bg-blue-950/40 rounded-sm"
                        data-testid={`doc-download-${d.id}`}
                        title="Görüntüle / İndir"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                      <button
                        onClick={() => remove(d.id)}
                        className="p-1.5 text-rose-400 hover:bg-rose-950/40 rounded-sm"
                        data-testid={`doc-delete-${d.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// PUANTAJ
// ============================================================================
function AttendanceTab({ employeeId, onChange }) {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ date: "", check_in: "08:30", check_out: "18:00", break_minutes: 60, status: "mesai", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [y, m] = period.split("-");
    const start = `${period}-01`;
    const ny = m === "12" ? Number(y) + 1 : Number(y);
    const nm = m === "12" ? "01" : String(Number(m) + 1).padStart(2, "0");
    const end = new Date(`${ny}-${nm}-01`);
    end.setDate(end.getDate() - 1);
    const endStr = end.toISOString().slice(0, 10);
    try {
      const [r, s] = await Promise.all([
        api.get(`/hr/employees/${employeeId}/attendance?start=${start}&end=${endStr}`),
        api.get(`/hr/employees/${employeeId}/attendance/summary?period=${period}`),
      ]);
      setRows(r.data || []);
      setSummary(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [employeeId, period]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.date) { toast.error("Tarih zorunlu"); return; }
    setSaving(true);
    try {
      await api.post(`/hr/attendance`, { employee_id: employeeId, ...form });
      toast.success("Kaydedildi");
      setDialogOpen(false);
      setForm({ date: "", check_in: "08:30", check_out: "18:00", break_minutes: 60, status: "mesai", note: "" });
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (aid) => {
    if (!window.confirm("Bu puantaj silinsin mi?")) return;
    try { await api.delete(`/hr/attendance/${aid}`); load(); onChange?.(); toast.success("Silindi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-sm border-border p-4 flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs">Dönem</Label>
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
            className="rounded-sm mt-1 w-40" data-testid="att-period" />
        </div>
        <Button onClick={() => setDialogOpen(true)} className="rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="add-attendance-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Puantaj Ekle
        </Button>
      </Card>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Toplam Çalışma" value={`${summary.total_worked_hours} sa`} hint={`${summary.day_count} gün kayıt`} accent="blue" />
          <StatCard label="Fazla Mesai" value={`${summary.total_overtime_hours} sa`} hint={`${summary.total_overtime_minutes} dk`} accent="orange" />
          <StatCard label="İzinli Gün" value={summary.status_counts.izinli || 0} hint="Bu ay" accent="emerald" />
          <StatCard label="Gelmeyen" value={summary.status_counts.yok || 0} hint="Bu ay" accent="amber" />
        </div>
      )}

      <Card className="rounded-sm border-border overflow-hidden">
        {rows.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">Bu ay için puantaj kaydı yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-left px-3 py-2">Giriş</th>
                <th className="text-left px-3 py-2">Çıkış</th>
                <th className="text-right px-3 py-2">Mola (dk)</th>
                <th className="text-right px-3 py-2">Çalışma</th>
                <th className="text-right px-3 py-2">Fazla Mesai</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="text-left px-3 py-2">Not</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`att-row-${r.id}`}>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.date}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.check_in || "—"}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.check_out || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{r.break_minutes}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{fmtMin(r.worked_minutes)}</td>
                  <td className={`px-3 py-1.5 text-right font-mono text-xs ${r.overtime_minutes > 0 ? "text-blue-400 font-bold" : "text-muted-foreground"}`}>
                    {r.overtime_minutes > 0 ? fmtMin(r.overtime_minutes) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    <span className="text-[10px] font-mono uppercase">{r.status}</span>
                  </td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.note || "—"}</td>
                  <td className="px-3 py-1.5">
                    <button onClick={() => remove(r.id)} className="text-slate-500 hover:text-rose-500 p-1">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="attendance-dialog">
          <DialogHeader>
            <DialogTitle>Puantaj Ekle / Güncelle</DialogTitle>
            <DialogDescription className="text-xs">Aynı tarih için mevcut kayıt varsa üzerine yazılır. Çalışma süresi = (çıkış - giriş) - mola. 8 saati aşan kısım fazla mesai olarak kaydedilir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Tarih *" type="date" value={form.date} onChange={(v) => setForm((p) => ({ ...p, date: v }))} testId="att-date" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Giriş" type="time" value={form.check_in} onChange={(v) => setForm((p) => ({ ...p, check_in: v }))} testId="att-in" />
              <Field label="Çıkış" type="time" value={form.check_out} onChange={(v) => setForm((p) => ({ ...p, check_out: v }))} testId="att-out" />
            </div>
            <Field label="Mola (dk)" type="number" value={form.break_minutes} onChange={(v) => setForm((p) => ({ ...p, break_minutes: Number(v) || 0 }))} testId="att-break" />
            <SelectField label="Durum" value={form.status} onChange={(v) => setForm((p) => ({ ...p, status: v }))}
              options={[
                { value: "mesai", label: "Mesai" },
                { value: "izinli", label: "İzinli" },
                { value: "yok", label: "Gelmedi" },
                { value: "resmi_tatil", label: "Resmi Tatil" },
              ]} testId="att-status" />
            <div>
              <Label className="text-xs">Not</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                className="rounded-sm mt-1" data-testid="att-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="rounded-sm">İptal</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-500" data-testid="att-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// İZİN
// ============================================================================
function LeavesTab({ employeeId, onChange }) {
  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "yillik", start_date: "", end_date: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/hr/employees/${employeeId}/leaves`);
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Tarihler zorunlu"); return; }
    setSaving(true);
    try {
      await api.post(`/hr/employees/${employeeId}/leaves`, form);
      toast.success("İzin talebi oluşturuldu");
      setDialogOpen(false);
      setForm({ leave_type: "yillik", start_date: "", end_date: "", reason: "" });
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const decide = async (lid, status, reject_reason = "") => {
    try {
      await api.post(`/hr/leaves/${lid}/decision`, { status, reject_reason });
      toast.success(`İzin ${status === "onaylandi" ? "onaylandı" : "reddedildi"}`);
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (lid) => {
    if (!window.confirm("Bu izin kaydı silinsin mi?")) return;
    try { await api.delete(`/hr/leaves/${lid}`); load(); onChange?.(); toast.success("Silindi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setDialogOpen(true)} className="rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="new-leave-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Yeni İzin Talebi
        </Button>
      </div>

      <Card className="rounded-sm border-border overflow-hidden">
        {rows.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">İzin kaydı yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Tip</th>
                <th className="text-left px-3 py-2">Başlangıç</th>
                <th className="text-left px-3 py-2">Bitiş</th>
                <th className="text-right px-3 py-2">Gün</th>
                <th className="text-left px-3 py-2">Sebep</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="text-right px-3 py-2">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`leave-row-${r.id}`}>
                  <td className="px-3 py-1.5 text-xs">{LEAVE_TYPES[r.leave_type] || r.leave_type}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.start_date}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.end_date}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs font-bold">{r.day_count}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.reason || "—"}</td>
                  <td className="px-3 py-1.5">
                    <Badge className={`rounded-sm text-[10px] uppercase ${LEAVE_STATUS[r.status]?.color || ""}`}>
                      {LEAVE_STATUS[r.status]?.label || r.status}
                    </Badge>
                    {r.status === "reddedildi" && r.reject_reason && (
                      <div className="text-[10px] text-rose-400 mt-1">↳ {r.reject_reason}</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <div className="flex justify-end gap-1">
                      {r.status === "beklemede" && (
                        <>
                          <button onClick={() => decide(r.id, "onaylandi")} className="p-1 text-emerald-500 hover:bg-emerald-950/40 rounded-sm" title="Onayla" data-testid={`leave-approve-${r.id}`}>
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => { const reason = window.prompt("Red gerekçesi:"); if (reason !== null) decide(r.id, "reddedildi", reason); }} className="p-1 text-amber-500 hover:bg-amber-950/40 rounded-sm" title="Reddet" data-testid={`leave-reject-${r.id}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      <button onClick={() => remove(r.id)} className="p-1 text-slate-500 hover:text-rose-500 rounded-sm">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="leave-dialog">
          <DialogHeader>
            <DialogTitle>Yeni İzin Talebi</DialogTitle>
            <DialogDescription className="text-xs">Talebiniz oluşturulduğunda başlangıçta "Beklemede" durumunda olur. Yıllık izinlerde gün sayısı otomatik olarak yıllık haktan düşülür (onaylandığında).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <SelectField label="İzin Tipi" value={form.leave_type} onChange={(v) => setForm((p) => ({ ...p, leave_type: v }))}
              options={Object.entries(LEAVE_TYPES).map(([k, v]) => ({ value: k, label: v }))} testId="leave-type" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Başlangıç *" type="date" value={form.start_date} onChange={(v) => setForm((p) => ({ ...p, start_date: v }))} testId="leave-start" />
              <Field label="Bitiş *" type="date" value={form.end_date} onChange={(v) => setForm((p) => ({ ...p, end_date: v }))} testId="leave-end" />
            </div>
            <div>
              <Label className="text-xs">Sebep</Label>
              <Textarea rows={2} value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                className="rounded-sm mt-1" data-testid="leave-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="rounded-sm">İptal</Button>
            <Button onClick={create} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-500" data-testid="leave-create">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Talep Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// AVANS & BORDRO
// ============================================================================
function AdvancesAndPayrollTab({ employeeId, onChange }) {
  const [advances, setAdvances] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [advOpen, setAdvOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [advForm, setAdvForm] = useState({ amount: 0, currency: "TRY", reason: "" });
  const [payForm, setPayForm] = useState({
    period: new Date().toISOString().slice(0, 7), gross_salary: 0, overtime_pay: 0, bonus: 0,
    advance_deduction: 0, tax_deduction: 0, sgk_deduction: 0, other_deductions: 0, currency: "TRY", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        api.get(`/hr/employees/${employeeId}/advances`),
        api.get(`/hr/employees/${employeeId}/payroll`),
      ]);
      setAdvances(a.data || []);
      setPayroll(p.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const createAdv = async () => {
    setSaving(true);
    try {
      await api.post(`/hr/employees/${employeeId}/advances`, advForm);
      toast.success("Avans talebi oluşturuldu");
      setAdvOpen(false);
      setAdvForm({ amount: 0, currency: "TRY", reason: "" });
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const decideAdv = async (aid, status) => {
    try { await api.post(`/hr/advances/${aid}/decision`, { status }); load(); toast.success("Güncellendi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const createPay = async () => {
    setSaving(true);
    try {
      await api.post(`/hr/payroll`, { employee_id: employeeId, ...payForm });
      toast.success("Bordro kaydı oluşturuldu");
      setPayOpen(false);
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const markPaid = async (rid) => {
    try { await api.post(`/hr/payroll/${rid}/mark-paid`); load(); toast.success("Ödendi olarak işaretlendi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      {/* Avans bölümü */}
      <Card className="rounded-sm border-border">
        <div className="p-4 flex items-center justify-between border-b border-border">
          <h3 className="text-sm font-bold uppercase tracking-widest font-mono">💰 Avans Talepleri</h3>
          <Button size="sm" onClick={() => setAdvOpen(true)} className="rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="new-advance-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Yeni Avans
          </Button>
        </div>
        {advances.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">Avans talebi yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-right px-3 py-2">Tutar</th>
                <th className="text-left px-3 py-2">Sebep</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="text-right px-3 py-2">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {advances.map((a) => (
                <tr key={a.id} data-testid={`adv-row-${a.id}`}>
                  <td className="px-3 py-1.5 font-mono text-xs">{a.request_date}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold">₺{fmtMoney(a.amount)}</td>
                  <td className="px-3 py-1.5 text-xs">{a.reason || "—"}</td>
                  <td className={`px-3 py-1.5 text-xs font-bold ${ADVANCE_STATUS[a.status]?.color}`}>{ADVANCE_STATUS[a.status]?.label}</td>
                  <td className="px-3 py-1.5 text-right">
                    <div className="flex justify-end gap-1">
                      {a.status === "beklemede" && (
                        <>
                          <button onClick={() => decideAdv(a.id, "onaylandi")} className="p-1 text-emerald-500 hover:bg-emerald-950/40 rounded-sm" data-testid={`adv-approve-${a.id}`}><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={() => decideAdv(a.id, "reddedildi")} className="p-1 text-amber-500 hover:bg-amber-950/40 rounded-sm"><X className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                      {a.status === "onaylandi" && (
                        <button onClick={() => decideAdv(a.id, "odendi")} className="p-1 text-blue-500 hover:bg-blue-950/40 rounded-sm text-[10px] uppercase font-mono px-2" data-testid={`adv-pay-${a.id}`}>Ödendi</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Bordro bölümü */}
      <Card className="rounded-sm border-border">
        <div className="p-4 flex items-center justify-between border-b border-border">
          <h3 className="text-sm font-bold uppercase tracking-widest font-mono">📄 Bordro Kayıtları</h3>
          <Button size="sm" onClick={() => setPayOpen(true)} className="rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="new-payroll-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Yeni Bordro
          </Button>
        </div>
        {payroll.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">Bordro yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Dönem</th>
                <th className="text-right px-3 py-2">Brüt</th>
                <th className="text-right px-3 py-2">F. Mesai</th>
                <th className="text-right px-3 py-2">Prim</th>
                <th className="text-right px-3 py-2">Kesinti</th>
                <th className="text-right px-3 py-2">Net</th>
                <th className="text-left px-3 py-2">Ödendi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payroll.map((p) => {
                const deductions = (p.tax_deduction || 0) + (p.sgk_deduction || 0) + (p.other_deductions || 0) + (p.advance_deduction || 0);
                return (
                  <tr key={p.id} data-testid={`payroll-row-${p.id}`}>
                    <td className="px-3 py-1.5 font-mono text-xs font-bold">{p.period}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">₺{fmtMoney(p.gross_salary)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">₺{fmtMoney(p.overtime_pay)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">₺{fmtMoney(p.bonus)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs text-rose-400">₺{fmtMoney(deductions)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-400">₺{fmtMoney(p.net_salary)}</td>
                    <td className="px-3 py-1.5">
                      {p.paid ? (
                        <span className="text-[10px] uppercase font-mono text-emerald-400">✓ {p.paid_at?.slice(0, 10)}</span>
                      ) : (
                        <button onClick={() => markPaid(p.id)} className="text-[10px] uppercase font-mono text-blue-400 hover:text-blue-300" data-testid={`payroll-pay-${p.id}`}>Ödendi işaretle</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {/* Avans dialog */}
      <Dialog open={advOpen} onOpenChange={setAdvOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="advance-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Avans Talebi</DialogTitle>
            <DialogDescription className="text-xs">Onaylanan avans daha sonra bordro dönemine "Avans Kesintisi" olarak yansıtılır.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Tutar *" type="number" value={advForm.amount} onChange={(v) => setAdvForm((p) => ({ ...p, amount: Number(v) || 0 }))} testId="adv-amount" />
              <SelectField label="Para Birimi" value={advForm.currency} onChange={(v) => setAdvForm((p) => ({ ...p, currency: v }))}
                options={[{ value: "TRY", label: "₺ TRY" }, { value: "USD", label: "$ USD" }, { value: "EUR", label: "€ EUR" }]} testId="adv-currency" />
            </div>
            <div>
              <Label className="text-xs">Sebep</Label>
              <Textarea rows={2} value={advForm.reason} onChange={(e) => setAdvForm((p) => ({ ...p, reason: e.target.value }))} className="rounded-sm mt-1" data-testid="adv-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdvOpen(false)}>İptal</Button>
            <Button onClick={createAdv} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-500" data-testid="adv-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Talep Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bordro dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="payroll-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Bordro</DialogTitle>
            <DialogDescription className="text-xs">Net Maaş = (Brüt + Fazla Mesai + Prim) − (Avans Kes. + Gelir V. + SGK + Diğer). Otomatik hesaplanır.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dönem (YYYY-MM) *" type="month" value={payForm.period} onChange={(v) => setPayForm((p) => ({ ...p, period: v }))} testId="pay-period" />
            <SelectField label="Para Birimi" value={payForm.currency} onChange={(v) => setPayForm((p) => ({ ...p, currency: v }))}
              options={[{ value: "TRY", label: "₺ TRY" }, { value: "USD", label: "$ USD" }, { value: "EUR", label: "€ EUR" }]} testId="pay-currency" />
            <Field label="Brüt Maaş" type="number" value={payForm.gross_salary} onChange={(v) => setPayForm((p) => ({ ...p, gross_salary: Number(v) || 0 }))} testId="pay-gross" />
            <Field label="Fazla Mesai" type="number" value={payForm.overtime_pay} onChange={(v) => setPayForm((p) => ({ ...p, overtime_pay: Number(v) || 0 }))} testId="pay-overtime" />
            <Field label="Prim / Bonus" type="number" value={payForm.bonus} onChange={(v) => setPayForm((p) => ({ ...p, bonus: Number(v) || 0 }))} testId="pay-bonus" />
            <Field label="Avans Kesintisi" type="number" value={payForm.advance_deduction} onChange={(v) => setPayForm((p) => ({ ...p, advance_deduction: Number(v) || 0 }))} testId="pay-advance-ded" />
            <Field label="Gelir Vergisi" type="number" value={payForm.tax_deduction} onChange={(v) => setPayForm((p) => ({ ...p, tax_deduction: Number(v) || 0 }))} testId="pay-tax" />
            <Field label="SGK Kesintisi" type="number" value={payForm.sgk_deduction} onChange={(v) => setPayForm((p) => ({ ...p, sgk_deduction: Number(v) || 0 }))} testId="pay-sgk" />
            <Field label="Diğer Kesintiler" type="number" value={payForm.other_deductions} onChange={(v) => setPayForm((p) => ({ ...p, other_deductions: Number(v) || 0 }))} testId="pay-other" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPayOpen(false)}>İptal</Button>
            <Button onClick={createPay} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-500" data-testid="pay-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// PERFORMANS / UYARI
// ============================================================================
function PerformanceTab({ employeeId, onChange }) {
  const [warnings, setWarnings] = useState([]);
  const [perf, setPerf] = useState([]);
  const [wOpen, setWOpen] = useState(false);
  const [pOpen, setPOpen] = useState(false);
  const [wForm, setWForm] = useState({ date: new Date().toISOString().slice(0, 10), severity: "orta", subject: "", detail: "", action_taken: "", issued_by: "" });
  const [pForm, setPForm] = useState({ period: String(new Date().getFullYear()), score: 0, rating: "B", strengths: "", improvements: "", notes: "", reviewer_name: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, p] = await Promise.all([
        api.get(`/hr/employees/${employeeId}/warnings`),
        api.get(`/hr/employees/${employeeId}/performance`),
      ]);
      setWarnings(w.data || []);
      setPerf(p.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const createW = async () => {
    if (!wForm.subject.trim()) { toast.error("Konu zorunlu"); return; }
    setSaving(true);
    try {
      await api.post(`/hr/employees/${employeeId}/warnings`, wForm);
      toast.success("Kayıt oluşturuldu");
      setWOpen(false);
      setWForm({ date: new Date().toISOString().slice(0, 10), severity: "orta", subject: "", detail: "", action_taken: "", issued_by: "" });
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const createP = async () => {
    setSaving(true);
    try {
      await api.post(`/hr/employees/${employeeId}/performance`, pForm);
      toast.success("Değerlendirme kaydedildi");
      setPOpen(false);
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const removeW = async (id) => {
    if (!window.confirm("Bu kayıt silinsin mi?")) return;
    try { await api.delete(`/hr/warnings/${id}`); load(); toast.success("Silindi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeP = async (id) => {
    if (!window.confirm("Bu kayıt silinsin mi?")) return;
    try { await api.delete(`/hr/performance/${id}`); load(); toast.success("Silindi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      {/* Uyarı / Tutanak */}
      <Card className="rounded-sm border-border">
        <div className="p-4 flex items-center justify-between border-b border-border">
          <h3 className="text-sm font-bold uppercase tracking-widest font-mono">⚠️ Uyarı / Tutanak</h3>
          <Button size="sm" onClick={() => setWOpen(true)} className="rounded-sm bg-rose-600 hover:bg-rose-500" data-testid="new-warning-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Yeni Kayıt
          </Button>
        </div>
        {warnings.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">Uyarı yok — temiz sicil ✓</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-left px-3 py-2">Ağırlık</th>
                <th className="text-left px-3 py-2">Konu</th>
                <th className="text-left px-3 py-2">Detay</th>
                <th className="text-left px-3 py-2">Alınan Aksiyon</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {warnings.map((w) => (
                <tr key={w.id} data-testid={`warn-row-${w.id}`}>
                  <td className="px-3 py-1.5 font-mono text-xs">{w.date}</td>
                  <td className={`px-3 py-1.5 text-xs font-bold ${SEVERITY[w.severity]?.color}`}>{SEVERITY[w.severity]?.label}</td>
                  <td className="px-3 py-1.5 text-sm">{w.subject}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground max-w-xs">{w.detail || "—"}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{w.action_taken || "—"}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button onClick={() => removeW(w.id)} className="p-1 text-slate-500 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Performans */}
      <Card className="rounded-sm border-border">
        <div className="p-4 flex items-center justify-between border-b border-border">
          <h3 className="text-sm font-bold uppercase tracking-widest font-mono">📈 Performans Değerlendirmeleri</h3>
          <Button size="sm" onClick={() => setPOpen(true)} className="rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="new-perf-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Yeni Değerlendirme
          </Button>
        </div>
        {perf.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">Değerlendirme yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Dönem</th>
                <th className="text-right px-3 py-2">Puan</th>
                <th className="text-left px-3 py-2">Not</th>
                <th className="text-left px-3 py-2">Güçlü Yönler</th>
                <th className="text-left px-3 py-2">Gelişim</th>
                <th className="text-left px-3 py-2">Değerlendiren</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {perf.map((p) => (
                <tr key={p.id} data-testid={`perf-row-${p.id}`}>
                  <td className="px-3 py-1.5 font-mono text-xs font-bold">{p.period}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{p.score}/100</td>
                  <td className="px-3 py-1.5 text-xs">
                    <span className="px-2 py-0.5 rounded-sm bg-blue-950 text-blue-300 font-bold">{p.rating || "—"}</span>
                  </td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground max-w-xs">{p.strengths || "—"}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground max-w-xs">{p.improvements || "—"}</td>
                  <td className="px-3 py-1.5 text-xs">{p.reviewer_name || "—"}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button onClick={() => removeP(p.id)} className="p-1 text-slate-500 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Uyarı dialog */}
      <Dialog open={wOpen} onOpenChange={setWOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="warning-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Uyarı / Tutanak</DialogTitle>
            <DialogDescription className="text-xs">Ağırlık: Düşük (sözlü uyarı), Orta (yazılı uyarı), Yüksek (ciddi ihmal), Tutanak (disiplin süreci başlatan resmi belge).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Tarih *" type="date" value={wForm.date} onChange={(v) => setWForm((p) => ({ ...p, date: v }))} testId="w-date" />
              <SelectField label="Ağırlık" value={wForm.severity} onChange={(v) => setWForm((p) => ({ ...p, severity: v }))}
                options={Object.entries(SEVERITY).map(([k, v]) => ({ value: k, label: v.label }))} testId="w-severity" />
            </div>
            <Field label="Konu *" value={wForm.subject} onChange={(v) => setWForm((p) => ({ ...p, subject: v }))} testId="w-subject" />
            <div>
              <Label className="text-xs">Detay</Label>
              <Textarea rows={2} value={wForm.detail} onChange={(e) => setWForm((p) => ({ ...p, detail: e.target.value }))} className="rounded-sm mt-1" data-testid="w-detail" />
            </div>
            <div>
              <Label className="text-xs">Alınan Aksiyon</Label>
              <Textarea rows={2} value={wForm.action_taken} onChange={(e) => setWForm((p) => ({ ...p, action_taken: e.target.value }))} className="rounded-sm mt-1" data-testid="w-action" />
            </div>
            <Field label="Düzenleyen" value={wForm.issued_by} onChange={(v) => setWForm((p) => ({ ...p, issued_by: v }))} testId="w-issued-by" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWOpen(false)}>İptal</Button>
            <Button onClick={createW} disabled={saving} className="rounded-sm bg-rose-600 hover:bg-rose-500" data-testid="w-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Performans dialog */}
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="performance-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Performans Değerlendirmesi</DialogTitle>
            <DialogDescription className="text-xs">Dönem: yıllık (örn 2026) veya çeyreklik (örn 2026-Q3). Not: A (mükemmel), B (iyi), C (geliştirilmesi gerek), D (yetersiz).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <Field label="Dönem *" value={pForm.period} onChange={(v) => setPForm((p) => ({ ...p, period: v }))} testId="p-period" />
              <Field label="Puan (0-100)" type="number" value={pForm.score} onChange={(v) => setPForm((p) => ({ ...p, score: Number(v) || 0 }))} testId="p-score" />
              <SelectField label="Not" value={pForm.rating} onChange={(v) => setPForm((p) => ({ ...p, rating: v }))}
                options={["A","B","C","D"].map((r) => ({ value: r, label: r }))} testId="p-rating" />
            </div>
            <div>
              <Label className="text-xs">Güçlü Yönler</Label>
              <Textarea rows={2} value={pForm.strengths} onChange={(e) => setPForm((p) => ({ ...p, strengths: e.target.value }))} className="rounded-sm mt-1" data-testid="p-strengths" />
            </div>
            <div>
              <Label className="text-xs">Gelişim Alanları</Label>
              <Textarea rows={2} value={pForm.improvements} onChange={(e) => setPForm((p) => ({ ...p, improvements: e.target.value }))} className="rounded-sm mt-1" data-testid="p-improvements" />
            </div>
            <Field label="Değerlendiren" value={pForm.reviewer_name} onChange={(v) => setPForm((p) => ({ ...p, reviewer_name: v }))} testId="p-reviewer" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPOpen(false)}>İptal</Button>
            <Button onClick={createP} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-500" data-testid="p-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// -------------------------------------------------------------------------
// ERP Erişimini Kes butonu — yalnızca admin/İK yöneticisi görebilir
// -------------------------------------------------------------------------
function RevokeAccessButton({ employee }) {
  const [me, setMe] = React.useState(null);
  const [user, setUser] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch { }
      if (employee?.email) {
        try {
          const { data: users } = await api.get("/users");
          const match = (users || []).find((u) => (u.email || "").toLowerCase() === (employee.email || "").toLowerCase());
          if (match) setUser(match);
        } catch { }
      }
    })();
  }, [employee?.email]);

  const isAdmin = me && (me.role === "admin" || me.role === "sistem_yoneticisi");
  if (!isAdmin || !user) return null;

  const revoked = user.is_active === false;

  const doRevoke = async () => {
    if (!reason.trim() || reason.trim().length < 3) { toast.error("Gerekçe en az 3 karakter olmalı"); return; }
    try {
      setBusy(true);
      const { data } = await api.post(`/users/${user.id}/revoke-access`, { reason });
      toast.success(data.message || "ERP erişimi kesildi");
      setOpen(false); setReason("");
      // refresh user
      const { data: u } = await api.get("/users");
      setUser(u.find((x) => x.id === user.id) || user);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doRestore = async () => {
    try {
      setBusy(true);
      await api.post(`/users/${user.id}/restore-access`);
      toast.success("ERP erişimi geri açıldı");
      const { data: u } = await api.get("/users");
      setUser(u.find((x) => x.id === user.id) || user);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (revoked) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={doRestore}
        disabled={busy}
        className="border-emerald-700 text-emerald-300 rounded-sm ml-2"
        data-testid="restore-access-btn"
      >
        Erişimi Geri Aç
      </Button>
    );
  }
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="border-rose-800 text-rose-300 hover:bg-rose-950 rounded-sm ml-2"
        data-testid="revoke-access-btn"
      >
        ERP Erişimini Kes
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-rose-300">ERP Erişimini Kes</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Bu işlem <b>{user.email}</b> kullanıcısının:
              <ul className="list-disc pl-4 mt-1 space-y-0.5">
                <li>Aktif oturumlarını hemen sonlandırır</li>
                <li>Yeni girişini engeller</li>
                <li>Mevcut token/refresh_token'larını iptal eder</li>
                <li>Güvenlik loguna kaydedilir</li>
              </ul>
              <div className="mt-2 text-amber-300">
                Google Workspace, Gmail vb. dış servisler bu işlemle kapatılmaz.
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-slate-300">Gerekçe (zorunlu)</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Örn. İşten ayrıldı — 27.08.2026"
              className="bg-slate-950 border-slate-700 rounded-sm"
              data-testid="revoke-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-700 rounded-sm" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-500 rounded-sm"
              onClick={doRevoke}
              disabled={busy}
              data-testid="revoke-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Erişimi Kes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
